import type { ArtifactStore } from '@railgun-community/wallet'
import { Buffer } from 'buffer'
import leveljs from 'level-js'
import {
  fetchVerifiedArtifact,
  fetchVerifiedVkey,
  getArtifactCacheVersion,
  isPinnedVkey
} from '@web/utils/railgunArtifactSources'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import levelup from 'levelup'

/**
 * Legacy IndexedDB-only artifact store: every artifact (wasm/vkey/zkey) is
 * fetched lazily by the SDK from its public IPFS gateway, SHA-256-verified, then
 * cached here. Used in contexts WITHOUT bundled artifacts — the mobile-web SPA
 * (no `/artifacts/` copied into the build) and tests. The extension uses the
 * hybrid `createBundledArtifactStore` instead.
 *
 * Kept in its own module (no `railgunCore` import) so the scan worker can use it
 * without pulling in chrome/ambire-network code (see railgunScanWorker.worker.ts
 * header — it intentionally avoids importing railgunCore).
 */
// `artifacts-v2.1/<variant>/<file>` or `artifacts-v2.1/poi-nov-2-23/<variant>/<file>`
function parseArtifactPath(path: string): { variant: string; file: string } | null {
  const [file, variant] = path.split('/').reverse()
  if (!file || !variant) return null
  return { variant, file }
}

// The SDK reuses one store path per variant across releases (SDK 11 regenerated the POI
// circuits under the same paths); versioning the key keeps old files from being mixed in.
function cacheKey(path: string): string {
  const parsed = parseArtifactPath(path)
  return parsed ? `${path}@${getArtifactCacheVersion(parsed.variant)}` : path
}

async function fetchFromFallbacks(path: string): Promise<Buffer | null> {
  const parsed = parseArtifactPath(path)
  if (!parsed) return null
  const log = (msg: string) => console.warn(`[RailgunArtifacts] ${msg}`)
  if (parsed.file === 'zkey' || parsed.file === 'wasm') {
    return fetchVerifiedArtifact(parsed.variant, parsed.file, log)
  }
  if (parsed.file === 'vkey.json') {
    const text = await fetchVerifiedVkey(parsed.variant)
    return text ? Buffer.from(text, 'utf8') : null
  }
  return null
}

export async function createBrowserArtifactStore(artifactDbName: string): Promise<ArtifactStore> {
  const { ArtifactStore } = await loadRailgunSdk()
  const db = levelup(leveljs(artifactDbName) as Parameters<typeof levelup>[0])

  const getFile = async (path: string): Promise<Buffer> => {
    const data = await db.get(cacheKey(path))
    return Buffer.isBuffer(data) ? data : Buffer.from(data as ArrayBuffer)
  }

  const storeFile = async (
    _dir: string,
    path: string,
    item: string | Uint8Array
  ): Promise<void> => {
    const data = typeof item === 'string' ? Buffer.from(item, 'utf8') : Buffer.from(item)
    // The SDK hash-checks zkey/wasm downloads but not vkeys; never cache an unpinned one.
    const parsed = parseArtifactPath(path)
    if (
      parsed?.file === 'vkey.json' &&
      !(await isPinnedVkey(parsed.variant, data.toString('utf8')))
    ) {
      throw new Error(`Downloaded verification key for ${parsed.variant} does not match its pin`)
    }
    await db.put(cacheKey(path), data)
  }

  const fileExists = async (path: string): Promise<boolean> => {
    const key = cacheKey(path)
    try {
      await db.get(key)
      return true
    } catch {
      // Not cached: try the fallback gateways (hash-verified) before letting the SDK use
      // its single hardcoded gateway.
      const data = await fetchFromFallbacks(path)
      if (!data) return false
      await db.put(key, data)
      return true
    }
  }

  return new ArtifactStore(getFile, storeFile, fileExists)
}
